/* eslint-disable no-underscore-dangle */
import { posthog } from 'posthog-js';
import store from '../../store';
import {
  CrashReportFeedbackPayload,
  PosthogErrorContext,
  PosthogMessageContext,
} from '../components/crash-report-detail-form/types';

const shouldReport = (productionOnly = true): boolean =>
  !productionOnly || process.env.NODE_ENV === 'production';

const isPosthogLoaded = (): boolean => Boolean((posthog as any).__loaded);

/**
 * Capture via posthog-js SDK so events link to session recordings.
 * trackPosthogEvent (direct fetch) does not attach to replay sessions.
 */
const capturePosthogEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!isPosthogLoaded()) {
    return;
  }

  posthog.capture(eventName, properties);
};

const getUserContext = (): Record<string, unknown> => ({
  environment: process.env.REACT_APP_ENV,
  user_email: store.getState()?.home?.email,
  $current_url: window.location.href,
});

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const buildExceptionProperties = (
  message: string,
  type: string,
  level: PosthogErrorContext['level'],
  context: PosthogErrorContext = {},
  stack?: string,
): Record<string, unknown> => ({
  $exception_message: message,
  $exception_type: type,
  $exception_level: level || 'error',
  ...(stack ? { $exception_stack_trace_raw: stack } : {}),
  ...getUserContext(),
  ...(context.tags || {}),
  ...(context.extra || {}),
  ...(context.componentStack
    ? { component_stack: context.componentStack }
    : {}),
  ...(context.fingerprint ? { crash_report_id: context.fingerprint } : {}),
});

/**
 * Report an exception to PostHog error tracking (replaces Sentry.captureException).
 */
export const capturePosthogException = (
  error: unknown,
  context: PosthogErrorContext = {},
): void => {
  if (!shouldReport(context.productionOnly !== false)) {
    return;
  }

  const err = normalizeError(error);

  capturePosthogEvent(
    '$exception',
    buildExceptionProperties(
      err.message,
      err.name,
      context.level || 'error',
      context,
      err.stack,
    ),
  );
};
/**
 * Report a non-exception message to PostHog (replaces Sentry.captureMessage).
 */
export const capturePosthogMessage = (
  message: string,
  context?: PosthogMessageContext,
): void => {
  let level: PosthogErrorContext['level'] = 'warning';
  let errorContext: PosthogErrorContext = {};

  if (typeof context === 'string') {
    level = context;
  } else if (context) {
    errorContext = context;
    level = context.level || 'warning';
  }

  if (!shouldReport(errorContext.productionOnly !== false)) {
    return;
  }

  capturePosthogEvent(
    '$exception',
    buildExceptionProperties(message, 'Message', level, errorContext),
  );
};

export const generateCrashReportId = (): string =>
  `crash_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/**
 * Submit optional user feedback linked to a crash report (replaces Sentry showDialog).
 */
export const submitCrashReportFeedback = ({
  reportId,
  feedback,
  errorMessage,
  componentStack,
}: CrashReportFeedbackPayload): void => {
  capturePosthogEvent('crash_report_feedback', {
    report_id: reportId,
    user_feedback: feedback,
    error_message: errorMessage,
    component_stack: componentStack,
    ...getUserContext(),
  });
};
